import { useState, useCallback, useRef, useEffect } from 'react';
import type { Socket } from 'socket.io-client';
import type { Measurement, CameraCalibration } from '../types';

interface UseMeasurementProps {
  roomId: string;
  isHost: boolean;
  socketRef: React.MutableRefObject<Socket | null>;
}

export function useMeasurement({
  socketRef
}: UseMeasurementProps) {
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [calibration, setCalibration] = useState<CameraCalibration | null>(null);
  const [guestDeviceModel, setGuestDeviceModel] = useState<string>('Unknown');

  const BASE_URL = import.meta.env.VITE_SIGNALING_SERVER_URL!;

  // Fetch calibration for guest device
  const fetchCalibration = useCallback(async (deviceModel: string) => {
    try {
      console.log(`Fetching calibration for device: ${deviceModel}`);
      const response = await fetch(
        `${BASE_URL}/api/measurement/device-specs/${encodeURIComponent(deviceModel)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      setCalibration({
        deviceModel: data.device,
        focalLengthMm: data.specs.focal_length_mm,
        sensorWidthMm: data.specs.sensor_width_mm,
        sensorHeightMm: data.specs.sensor_height_mm,
        horizontalFovDegrees: data.specs.horizontal_fov_degrees,
        isDefault: !data.found
      });

      console.log('✅ Calibration loaded:', data.device, data.found ? '(specific)' : '(fallback)');
      return data;
    } catch (error) {
      console.error('Failed to fetch calibration:', error);
      return null;
    }
  }, [BASE_URL]);


  // Clear all measurements
  const clearMeasurements = useCallback(() => {
    setMeasurements([]);
    console.log('🗑️ Cleared all measurements');
  }, []);

  // Listen for measurement results from BOTH host and guest
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleMeasurementResult = (payload: { measurement: Measurement }) => {
      const measurement = payload.measurement;

      setMeasurements(prev => [...prev, measurement]);

      const initiator = measurement.initiatedBy === 'guest' ? 'Guest' : 'Host';
      const distance = measurement.real_distance_inches?.toFixed(1);

      console.log(
        `✅ ${initiator} measured: ${distance}" (${measurement.real_distance_cm?.toFixed(1)}cm)`
      );
    };

    socket.on('measurement-result', handleMeasurementResult);

    return () => {
      socket.off('measurement-result', handleMeasurementResult);
    };
  }, [socketRef]);

  return {
    measurements,
    calibration,
    clearMeasurements,
    guestDeviceModel,
    setGuestDeviceModel,
    fetchCalibration,
  };
}
